import type ReadingScale from 'teacher-ui-client/models/book-library/reading-scale'
import type BookLibraryBookMetadata from 'teacher-ui-client/models/book-library/book-metadata'
import { isPresent } from '@ember/utils'

export const FILTER_TYPES = {
  READING_LEVEL: 'blake-reading-level',
  LEXILE: 'lexile-level',
  DRA: 'dra',
  BOOK_COLOR: 'book-color',
  FP_AZ: 'fp-az',
  RR_LEVEL: 'rr-level',
  YEAR_LEVEL: 'year-level',
  READING_AGE: 'reading-age',
  FP_PEAKS: 'fp-peaks',
} as const

type ValueOf<T> = T[keyof T]
export type FilterKey = ValueOf<typeof FILTER_TYPES>

// A map of filter keys to their corresponding reading scale
export const SCALE_ATTRIBUTE_MAP: Partial<Record<FilterKey, keyof ReadingScale>> = {
  [FILTER_TYPES.READING_LEVEL]: 'blakeReadingLevel',
  [FILTER_TYPES.LEXILE]: 'lexileRange',
  [FILTER_TYPES.DRA]: 'dra',
  [FILTER_TYPES.BOOK_COLOR]: 'bookColour',
  [FILTER_TYPES.FP_AZ]: 'fpAz',
  [FILTER_TYPES.RR_LEVEL]: 'rrLevel',
  [FILTER_TYPES.YEAR_LEVEL]: 'yearLevel',
}

// A map of filter keys to their corresponding book metadata attribute name.
export const METADATA_ATTRIBUTE_MAP: Record<FilterKey, keyof BookLibraryBookMetadata> = {
  [FILTER_TYPES.READING_LEVEL]: 'blakeReadingLevel',
  [FILTER_TYPES.LEXILE]: 'lexileRange',
  [FILTER_TYPES.DRA]: 'dra',
  [FILTER_TYPES.BOOK_COLOR]: 'bookColour',
  [FILTER_TYPES.FP_AZ]: 'fpAz',
  [FILTER_TYPES.RR_LEVEL]: 'rrLevel',
  [FILTER_TYPES.YEAR_LEVEL]: 'yearLevel',
  [FILTER_TYPES.READING_AGE]: 'readingAge',
  [FILTER_TYPES.FP_PEAKS]: 'fpPeaks',
}

interface AttributeRange {
  lower: keyof ReadingScale
  upper: keyof ReadingScale
}

// A map of filter keys to their corresponding reading scale attribute range keys
// Only necessary for reading scales that are "grouped", containing lower and upper range keys
export const SCALE_ATTRIBUTE_RANGE_MAP: Partial<Record<FilterKey, AttributeRange>> = {
  [FILTER_TYPES.LEXILE]: {
    lower: 'lexileRangeLower',
    upper: 'lexileRangeUpper',
  },
}

export interface PrimaryFilterOption {
  label: string
  searchMin: number
  searchMax: number
  rangeLabels?: {
    min: string
    max: string
  }
}

type PrimaryFilterOptions = Partial<Record<FilterKey, PrimaryFilterOption[]>>

export interface PrimaryFilter {
  id: FilterKey
  translationKey: string
  searchKey: string
  options: PrimaryFilterOption[]
}

/**
 * Build primary filter objects out of the given reading scale records
 *
 * The goal is something like this;
 * [
 *   {
 *     id,
 *     searchKey: 'blake-reading-level', // used in the search query
 *     translationKey: '', // an intl translation key for the filter label
 *     options: [
 *       { label: 'A', searchMin: 0, searchMax: 3 }, // label used on the slider display
 *       { label: 'B', searchMin: 4, searchMax: 4 }, // search values used in the search query
 *       ...
 *     ]
 *   }
 * ]
 */
export function buildPrimaryFilters(readingScales: ReadingScale[]): PrimaryFilter[] {
  const primaryFilterOptions: PrimaryFilterOptions = {}

  // Make sure to sort the scales - they may be provided by the backend out of order. This will ensure the options are
  // presented in the correct order to the slider, and when making requests based on the selected slider option.
  const sortedReadingScales = [...readingScales].sort((a, b) => {
    if (a.blakeReadingLevel === b.blakeReadingLevel) return 0
    return a.blakeReadingLevel < b.blakeReadingLevel ? -1 : 1
  })

  sortedReadingScales.forEach((scale) => {
    const searchValue = scale.blakeReadingLevel

    const filterScaleEntries = Object.entries(SCALE_ATTRIBUTE_MAP) as [FilterKey, keyof ReadingScale][]
    filterScaleEntries.forEach(([filterKey, scaleAttr]: [FilterKey, keyof ReadingScale]) => {
      // some reading scale attributes can be nullish, if they're not valid for this teacher
      if (typeof scale[scaleAttr] === 'string' || typeof scale[scaleAttr] === 'number') {
        const label = `${scale[scaleAttr]}`
        const existingOptionForLabel = primaryFilterOptions[filterKey]?.find((option) => option.label === label)
        if (existingOptionForLabel) {
          // update existing option
          if (searchValue < existingOptionForLabel.searchMin) existingOptionForLabel.searchMin = searchValue
          if (searchValue > existingOptionForLabel.searchMax) existingOptionForLabel.searchMax = searchValue
        } else {
          // push new option
          const option: PrimaryFilterOption = {
            label,
            searchMin: searchValue,
            searchMax: searchValue,
          }

          // Handle range labels
          if (Object.keys(SCALE_ATTRIBUTE_RANGE_MAP).includes(filterKey)) {
            const rangeAttrs = SCALE_ATTRIBUTE_RANGE_MAP[filterKey]

            if (rangeAttrs?.lower && rangeAttrs.upper) {
              const min = scale[rangeAttrs.lower]
              const max = scale[rangeAttrs.upper]
              if (typeof min === 'string' && typeof max === 'string') {
                option.rangeLabels = { min, max }
              }
            }
          }

          // add new option to filter options array
          primaryFilterOptions[filterKey] = [...(primaryFilterOptions[filterKey] ?? []), option]
        }
      }
    })
  })

  const searchKey = 'blake-reading-level'
  const filterKeys = Object.values(FILTER_TYPES) as string[]
  return Object.entries(primaryFilterOptions) // object.entries loses key type FilterKey, hence filter type predicate
    .filter((entry): entry is [FilterKey, PrimaryFilterOption[]] => filterKeys.includes(entry[0]))
    .map(([filterType, options]) => ({
      id: filterType,
      searchKey,
      translationKey: `bookLibrary.primaryFilters.${filterType}`,
      options,
    }))
}

/**
 * Reading age is not part of the reading scales system, but rather just an option we can filter by. Build it manually
 */
export const readingAgePrimaryFilter: PrimaryFilter = {
  id: FILTER_TYPES.READING_AGE,
  searchKey: 'reading-age',
  translationKey: `bookLibrary.primaryFilters.${FILTER_TYPES.READING_AGE}`,
  options: [
    { label: '5', searchMin: 5, searchMax: 5 },
    { label: '6', searchMin: 6, searchMax: 6 },
    { label: '7', searchMin: 7, searchMax: 7 },
    { label: '8', searchMin: 8, searchMax: 8 },
    { label: '9', searchMin: 9, searchMax: 9 },
    { label: '10', searchMin: 10, searchMax: 10 },
    { label: '11', searchMin: 11, searchMax: 11 },
    { label: '12', searchMin: 12, searchMax: 12 },
    { label: '13', searchMin: 13, searchMax: 13 },
  ],
}

/**
 * Fast Phonics Peaks are not part of the reading scales system, but rather just an option we can filter by. Build it manually
 */
export const fastPhonicsPrimaryFilter: PrimaryFilter = {
  id: FILTER_TYPES.FP_PEAKS,
  searchKey: 'fp-peaks',
  translationKey: `bookLibrary.primaryFilters.${FILTER_TYPES.FP_PEAKS}`,
  options: [
    { label: '1', searchMin: 1, searchMax: 1 },
    { label: '2', searchMin: 2, searchMax: 2 },
    { label: '3', searchMin: 3, searchMax: 3 },
    { label: '4', searchMin: 4, searchMax: 4 },
    { label: '5', searchMin: 5, searchMax: 5 },
    { label: '6', searchMin: 6, searchMax: 6 },
    { label: '7', searchMin: 7, searchMax: 7 },
    { label: '8', searchMin: 8, searchMax: 8 },
    { label: '9', searchMin: 9, searchMax: 9 },
    { label: '10', searchMin: 10, searchMax: 10 },
    { label: '11', searchMin: 11, searchMax: 11 },
    { label: '12', searchMin: 12, searchMax: 12 },
    { label: '13', searchMin: 13, searchMax: 13 },
    { label: '14', searchMin: 14, searchMax: 14 },
    { label: '15', searchMin: 15, searchMax: 15 },
    { label: '16', searchMin: 16, searchMax: 16 },
    { label: '17', searchMin: 17, searchMax: 17 },
    { label: '18', searchMin: 18, searchMax: 18 },
    { label: '19', searchMin: 19, searchMax: 19 },
    { label: '20', searchMin: 20, searchMax: 20 },
  ],
}

/**
 * Filter & sort primary filters for display. We show primary filters for some locales and not others.
 * Intended for the teacher library search page.
 */
export function localisePrimaryFilters(primaryFilters: PrimaryFilter[], libraryAccent: string) {
  const { READING_LEVEL, LEXILE, RR_LEVEL, FP_AZ, READING_AGE, DRA, YEAR_LEVEL, BOOK_COLOR, FP_PEAKS } = FILTER_TYPES

  const sortOrders: Record<string, FilterKey[]> = {
    au: [READING_LEVEL, LEXILE, FP_PEAKS, RR_LEVEL, FP_AZ, READING_AGE, YEAR_LEVEL],
    us: [LEXILE, FP_PEAKS, READING_LEVEL, RR_LEVEL, READING_AGE, FP_AZ, DRA, YEAR_LEVEL],
    ca: [LEXILE, FP_PEAKS, READING_LEVEL, RR_LEVEL, READING_AGE, FP_AZ, DRA, YEAR_LEVEL],
    uk: [BOOK_COLOR, READING_LEVEL, LEXILE, FP_PEAKS, FP_AZ, READING_AGE, YEAR_LEVEL],
  }

  const sortOrder = sortOrders[libraryAccent] ?? [READING_LEVEL, READING_AGE]

  // keep primary filter if it's valid for the accent, and has options
  const filteredPrimaryFilters = primaryFilters.filter(
    (primaryFilter) => sortOrder.includes(primaryFilter.id) && isPresent(primaryFilter.options),
  )

  // Sort how we want them to appear for the given accent
  const sortedPrimaryFilters = filteredPrimaryFilters.sort((a, b) => {
    const aIndex = sortOrder.indexOf(a.id)
    const bIndex = sortOrder.indexOf(b.id)
    if (aIndex === bIndex) return 0
    return aIndex < bIndex ? -1 : 1
  })

  return sortedPrimaryFilters
}
